import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Animated as ReactAnimated,
  FlatList,
  Dimensions,
} from 'react-native';
import Animated, {
  Easing,
  measure,
  runOnUI,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { isTablet } from 'react-native-device-info';
import { EventRegister } from 'react-native-event-listeners';
import AppContext from '../../navigation/Context';
import colors from '../../resources/colors';
import images from '../../resources/images';
import { useToast } from 'react-native-toast-notifications';
import { format } from 'date-fns';
import { useNavigation } from '@react-navigation/core';
import DataController from '../../controllers/DataController';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { createStackNavigator } from '@react-navigation/stack';
import HTML from 'react-native-render-html';
import infoHTML from '../../resources/infoHTML';
import { WebView as WebViewWeb } from 'react-native-web-webview';
import WebView from 'react-native-webview';
import RNFS from 'react-native-fs';

const NotesOnLectionaryScreen = ({ navigation }) => {
  const toast = useToast();
  const context = useContext(AppContext);

  const [html, setHtml] = useState('');
  const [fontMultiplier, setFontMultiplier] = useState(1);

  const getFontMultiplier = async () => {
    const fontMultiplierInStorage = await DataController.getFontMultiplier();
    setFontMultiplier(fontMultiplierInStorage);
  };

  useEffect(() => {
    getFontMultiplier();
  }, [])

  useEffect(() => {
    const loadHTML = async (event, href) => {
      const file = Platform.OS === 'web' ? infoHTML.Notes : infoHTML.NotesNative;

      try {
        let uri;
        let html;

        if (Platform.OS === 'web') {
          html = file;
        } else {
          uri = Image.resolveAssetSource(file).uri;

          if (__DEV__) {
            html = await fetch(uri);
            html = await html.text();
          } else if (Platform.OS === 'ios') {
            // Release version - don't decode uri
            html = await RNFS.readFile(uri, 'utf8');
            // Debug version - need to decode uri
            // html = await RNFS.readFile(decodeURIComponent(uri), 'utf8');
          } else if (Platform.OS === 'android') {
            html = await RNFS.readFile(`android.resource://com.aimer.lectionary/raw/${uri}`, 'utf8');
            // html = await RNFS.readFile(`android.resource://com.aimer.lectionary.TEST/raw/${uri}`, 'utf8');
          }
        }

        setHtml(html);
      } catch (error) {
        console.log(error);
      }
    };

    loadHTML();
  }, []);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <View style={styles.headerRow}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Image style={styles.menuIcon} source={images.Button_Back} />
            <Text style={styles.backButtonText} allowFontScaling={false}>
              {'Back'}
            </Text>
          </TouchableOpacity>
        </View>
      ),
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle} allowFontScaling={false}>
            {'Notes on the Lectionary'}
          </Text>
        </View>
      ),
      headerRight: () => <View />,
      headerTitleAlign: 'center',
      headerStyle: {
        backgroundColor: colors.grey20,
        shadowRadius: 0,
        shadowOffset: {
          height: 0,
        },
        borderBottomWidth: 0.5,
        borderBottomColor: colors.grey50,
        height: Platform.OS === 'web' ? 44 : undefined,
      },
      headerTitleStyle: {
        justifyContent: 'center'
      },
    });
  }, [navigation]);

  const style = `
  <style type="text/css">
  @import url('https://fonts.googleapis.com/css2?family=Fira+Sans:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Neuton:ital,wght@0,200;0,300;0,400;0,700;0,800;1,400&display=swap');

  body {
    font-family: 'Fira Sans', sans-serif;
  }

  h1 {
    font-family: 'Fira Sans', sans-serif !important;
    font-size: 20px;
    font-weight: bold;
    line-height: 28px;
    letter-spacing: 0.1px;
    color: #880f30;
  }
  
  h2 {
    font-family: 'Fira Sans', sans-serif !important;
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
    letter-spacing: -0.1px;
    color: #212529;
  }

  p {
    font-size: 17px !important;
    line-height: 26px !important;
    letter-spacing: 0px;
    color: #212529;
  }

  a {
    text-decoration-line: underline;
    color: #007aff;
    cursor: pointer;
  }

  primary-icon {
    color: #880f30 !important;
  }

  red-text {
    color: #fe0000;
  }

  table,
  th,
  td {
    border-collapse: collapse;
    border-color: #999;
    font-size: 17px !important;
    font-weight: normal;
    padding: 5px;
  }

  .scrollable-table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }

  .extra-padding td {
    padding-left: 20px;
    padding-right: 20px;
  }

  .red-header-row {
    border-style: solid;
    border-top-width: 2px;
    border-bottom-width: 2px;
    border-top-color: #fe0000;
    border-bottom-color: #fe0000;
    border-left-width: 0px;
    border-right-width: 0px;
  }

  .red-border-top {
    border-style: solid;
    border-top-width: 2px;
    border-bottom-width: 0px;
    border-top-color: #fe0000;
    border-bottom-color: #fe0000;
    border-left-width: 0px;
    border-right-width: 0px;
  }

  th.extra-padding {
    padding-left: 20px;
    padding-right: 20px;
  }

  .red-header {
    color: #fe0000;
  }
</style>
`;

  return (
    <View style={styles.container}>
      {Platform.OS === 'web' ? (
        <WebViewWeb
          originWhitelist={['*']}
          style={{
            height: '100%',
            backgroundColor: colors.accentWhite,
          }}
          containerStyle={{
            flex: 0,
            maxWidth: 1400,
            alignSelf: 'center',
            justifyContent: 'center',
            alignItems: 'center',
            paddingHorizontal: Dimensions.get('window').width > 700 ? 30 : 15,
          }}
          source={{
            html: `
          <div 
          style="
          font-size: ${21 * fontMultiplier}px;
          max-width: 1400px; 
          align-self: center !important; 
          justify-content: center; 
          align-items: center; 
          margin: auto; 
          padding-left: ${Dimensions.get('window').width > 700 ? `${30}px` : `${15}px`};
          padding-right: ${Dimensions.get('window').width > 700 ? `${30}px` : `${15}px`};
          padding-bottom: 48px;
          ">
          ${html}
          </div>`
          }}
        />
      ) : (
        <WebView
          originWhitelist={['x']}
          style={{
            height: '100%',
            backgroundColor: colors.accentWhite,
          }}
          containerStyle={{
            width: '100%',
            height: '100%',
            maxWidth: 1400,
            backgroundColor: colors.accentWhite,
          }}
          source={{
            html: `
            <!DOCTYPE html>
            <html>
            <head>
              <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1">
              ${style}
            </head>
            <body>
            <div  
            style="
            max-width: 1400px; 
            align-self: center !important; 
            justify-content: center; 
            align-items: center; 
            margin: auto; 
            padding-top: 6px;
            padding-left: ${Dimensions.get('window').width > 700 ? `${30}px` : `${10}px`};
            padding-right: ${Dimensions.get('window').width > 700 ? `${30}px` : `${10}px`};
            padding-bottom: 48px;
            ">
            ${html}
            </div>
            </body>
            </html>`          }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.accentWhite,
    flex: 1,
    height: '100%',
  },
  backButton: {
    flexDirection: 'row',
    marginStart: 4,
    alignItems: 'center',
  },
  backButtonText: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 17,
    fontWeight: 'normal',
    color: colors.primary,
    marginStart: -5,
  },
  scrollViewContainer: {
    width: '100%',
    maxWidth: 1200,
    paddingTop: 20,
    paddingHorizontal: Dimensions.get('window').width > 700 ? 30 : 15,
    paddingBottom: 54,
    flex: 1,
    flexGrow: 1,
    alignSelf: 'center'
  },
  headerRow: {
    flexDirection: 'row',
  },
  buttonMenu: {
    width: 44,
    height: 44,
    marginStart: 4,
  },
  menuIcon: {
    width: 44,
    height: 44,
    tintColor: colors.primary,
  },
  headerTitleContainer: {
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    color: colors.primary,
  },
  header: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 12,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    letterSpacing: 0.2,
    color: colors.grey70,
    marginBottom: 4,
  },
  checkboxButton: {
    height: 60,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  checkboxText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    textAlign: 'center',
    color: colors.defaultText,
  },
  checkboxIcon: {
    height: 44,
    width: 44,
  },
  checkboxSeparator: {
    height: 1,
    backgroundColor: colors.grey50,
  },
  uncheckedIconContainer: {
    height: 44,
    width: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uncheckedIcon: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderRadius: 100,
    borderColor: colors.grey50
  },
  settingContainer: {
    marginTop: 24,
  },
  fontOuterContainer: {
    backgroundColor: colors.accentWhite,
    borderColor: colors.grey40,
    borderWidth: 1,
    borderRadius: 4,
    marginTop: 4,
    marginBottom: 8,
  },
  fontContainer: {
    flexDirection: 'row',
    height: 44,
  },
  fontButton: {
    width: 100,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fontIcon: {
    height: 44,
    width: 44,
  },
  fontSizeContainer: {
    flex: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: colors.grey40,
    justifyContent: 'center',
  },
  fontSizeText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: 0,
    textAlign: 'center',
    color: colors.grey70,
  },
  resetButtonContainer: {
    borderTopColor: colors.grey40,
    borderTopWidth: 1,
  },
  resetButton: {
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  resetButtonText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: 0.1,
    color: colors.defaultText,
  },
  textPreviewContainer: {
    backgroundColor: colors.accentWhite,
    borderColor: colors.grey40,
    borderWidth: 1,
    borderRadius: 4,
    marginTop: 4,
    padding: 12,
  },
  textPreview: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 16,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 22,
    letterSpacing: -0.1,
    color: colors.defaultText,
  },
});

export default NotesOnLectionaryScreen;
